import React, { useEffect, useContext } from 'react';
import { sortBy } from 'lodash';
import { stripTags } from '../../../../utils/strip_tags';
import { connect } from 'react-redux';
import { Card, Form, Row, Col, Button, Select } from '../../../ui-kit';
import { IconBars, IconTrash, IconPlus } from '../../../ui-kit/icons';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import { PriorityButtons } from '../../../activity-report-form/shared/Fields';
import { selectors as taskSelectors } from '../../../../state/modules/tasks';
import { selectors as activityTypeSelectors } from '../../../../state/modules/activity-types';
import Badge from '../../../ui-kit/Badge';
import AccessRightsContext from '../../../contexts/AccessRightsContext';

const DragHandle = SortableHandle(() => <IconBars />);

const ButtonCard = ({ tasks, activityTypes, button, handleButtonChanged, id, deleteButton }) => {
  const taskOptions = sortBy(
    tasks.filter(t => t.alertable),
    ['name']
  ).map(t => ({
    value: t.id,
    // search for task name, id and station names
    textLabel: [t.id, t.name, ...t.stations.map(g => g.name)].filter(Boolean).join(' '),
    label: (
      <>
        {[t.id, t.name].filter(Boolean).join(' - ')}
        {t.stations.map(g => (
          <Badge className="ms-1" key={g.id}>
            {g.name}
          </Badge>
        ))}
      </>
    )
  }));

  const activityTypeOptions = sortBy(activityTypes, ['name']).map(t => ({
    value: t.id,
    label: [t.id, t.name].filter(Boolean).join(' - ')
  }));

  // Chooses which elements to display as selected in the select input
  const selectedOptions = (collection, paramName) =>
    collection.filter(element => button[paramName].includes(element.value));

  const filterOption = (op, filterValue) => op.data.textLabel.toLowerCase().includes(filterValue.toLowerCase());

  // Event handlers for different inputs
  const handleFieldChanged = (field, event) => handleButtonChanged(id, { ...button, [field]: event.target.value });

  const handlePrioChanged = prio => handleButtonChanged(id, { ...button, prio });

  const handleSelectChange = (events, paramName) => {
    const values = events.map(e => e.value);
    button = { ...button, [paramName]: values };
    handleButtonChanged(id, button);
  };

  const renderSelectFor = (modelName, collection, paramName, errors) => {
    // TODO properly handle BS5 errors using ControlFeedback
    let translation = `activerecord.models.${modelName}`;
    if (typeof I18n.t(translation) !== 'string') translation += '.one';
    return (
      <Form.Group validationState={errors ? 'error' : null} className="mb-2">
        <label className="control-label">{I18n.t(translation)}</label>
        <Select
          value={selectedOptions(collection, paramName)}
          isMulti
          onChange={e => handleSelectChange(e, paramName)}
          options={collection}
          isSearchable={true}
          isClearable={true}
          filterOption={filterOption}
        />
        {errors && <Form.ControlFeedback type="invalid">{errors}</Form.ControlFeedback>}
      </Form.Group>
    );
  };

  const errors = {
    button_title: button.button_title.length === 0 ? I18n.t('errors.messages.blank') : null,
    message: button.message.length === 0 ? I18n.t('errors.messages.blank') : null,
    task_ids: button.task_ids.length === 0 ? I18n.t('errors.messages.blank') : null
  };

  const renderInputFor = (label, name, type = 'text') => (
    <Form.Group className="mb-2">
      <label className="control-label">{label}</label>
      <Form.InputGroup hasValidation>
        <Form.Control
          type={type}
          name={name}
          placeholder={label}
          value={button[name]}
          onChange={e => handleFieldChanged(name, e)}
          isInvalid={errors[name]}
        />
        {errors[name] && <Form.ControlFeedback type="invalid">{errors[name]}</Form.ControlFeedback>}
      </Form.InputGroup>
    </Form.Group>
  );

  return (
    <Card className="mb-4" style={{ zIndex: 9999 }}>
      <Card.Body>
        <Row>
          <Col sm={1} style={{ width: '3%' }}>
            <DragHandle />
          </Col>
          <Col>
            {useContext(AccessRightsContext).admin && (
              <div className="mb-5">
                {renderInputFor(
                  'Redirect URL. ADMIN ONLY: if set, the button will redirect ' +
                    'to the URL instead of creating an incident',
                  'url'
                )}
              </div>
            )}

            <Row>
              <Col sm="4">
                {renderInputFor(
                  I18n.t('dashboards.widget_archetypes.quick_incident_creation_buttons.settings.title'),
                  'button_title'
                )}
              </Col>
              <Col sm="2">
                {renderInputFor(
                  I18n.t('dashboards.widget_archetypes.quick_incident_creation_buttons.settings.color'),
                  'button_color',
                  'color'
                )}
              </Col>
              <Col sm="5">
                <label>{I18n.t('activerecord.attributes.incidents/incident.priority')}</label>
                <br />
                <PriorityButtons selected={button.prio} handleChange={handlePrioChanged} />
              </Col>
              <Col sm="1">
                <Button variant="danger" size="xs" onClick={() => deleteButton(id)} className="mb-1 float-right">
                  <IconTrash />
                </Button>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                {renderInputFor(I18n.t('activerecord.attributes.incidents/incident.message_body'), 'message')}
              </Col>
              <Col>{renderInputFor(I18n.t('activerecord.attributes.incidents/incident.location'), 'location')}</Col>
            </Row>
            <Row>
              <Col sm={6}>{renderSelectFor('task', taskOptions, 'task_ids', errors.task_ids)}</Col>
              <Col>{renderSelectFor('incidents/activity_type', activityTypeOptions, 'activity_type_ids')}</Col>
            </Row>
            <Form.InputGroup className="mb-2">
              <Form.Check
                checked={button.backup_alert || false}
                onChange={e => handleButtonChanged(id, { ...button, backup_alert: e.target.checked })}
                label={I18n.t('incidents.backup_alerting.label')}
              />
              <Form.Text className="ms-2">{stripTags(I18n.t('incidents.backup_alerting.description'))}</Form.Text>
            </Form.InputGroup>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const SortableButtonCard = SortableElement(props => <ButtonCard {...props} />);

const SortableButtonCardList = SortableContainer(props => (
  <div>
    {props.buttons.map((button, index) => (
      <SortableButtonCard
        key={index}
        id={index}
        index={index}
        button={button}
        deleteButton={props.deleteButton}
        handleButtonChanged={props.handleButtonChanged}
        tasks={props.tasks}
        activityTypes={props.activityTypes}
      />
    ))}
  </div>
));

const Settings = ({
  updateWidgetSettingsField,
  widgetSettings: { buttons },
  activityTypes,
  tasksWithGroups,
  dashboardAPIManager
}) => {
  useEffect(() => {
    dashboardAPIManager.fetchActivityTypes();
    dashboardAPIManager.fetchTasks();
  }, [dashboardAPIManager]);

  buttons = buttons || [];

  // Auxiliary functions
  const addNewButton = () => {
    const newButton = {
      id: globalThis.crypto.randomUUID(),
      button_title: '',
      button_color: '#ffffff',
      message: '',
      activity_type_ids: [],
      location: '',
      prio: null,
      backup_alert: false,
      task_ids: []
    };

    updateWidgetSettingsField('buttons', [...buttons, newButton]);
  };

  const deleteButton = index => {
    const toRemove = buttons[index];
    updateWidgetSettingsField(
      'buttons',
      buttons.filter(f => f !== toRemove)
    );
  };

  const handleButtonChanged = (index, button) =>
    updateWidgetSettingsField(
      'buttons',
      buttons.map((f, i) => (i === index ? button : f))
    );

  const onSortEnd = ({ oldIndex, newIndex }) =>
    updateWidgetSettingsField('buttons', arrayMove(buttons, oldIndex, newIndex));

  return (
    <>
      <SortableButtonCardList
        buttons={buttons}
        onSortEnd={onSortEnd}
        useDragHandle={true}
        deleteButton={deleteButton}
        handleButtonChanged={handleButtonChanged}
        tasks={tasksWithGroups}
        activityTypes={activityTypes}
      />
      <Button variant="success" onClick={addNewButton}>
        <IconPlus /> {I18n.t('dashboards.widget_archetypes.quick_incident_creation_buttons.new_button')}
      </Button>
    </>
  );
};

const mapStateToProps = state => ({
  tasksWithGroups: Object.values(taskSelectors.tasksWithGroups(state)),
  activityTypes: activityTypeSelectors.incidentActivityTypes(state)
});

export default connect(mapStateToProps)(Settings);
